import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { normalizeKycConfig } from '@formosa/shared/utils/kycConfig';
import { adminApi } from '@formosa/shared/api/admin';
import Pagination from '../../components/Pagination';
import MemberActionsMenu from './MemberActionsMenu';
import MemberAgentEditModal from './MemberAgentEditModal';
import MemberBatchModal from './MemberBatchModal';
import MemberInviterTreeModal from './MemberInviterTreeModal';
import MemberBalanceAdjustModal from './MemberBalanceAdjustModal';
import MemberPasswordModal from './MemberPasswordModal';
import MemberRemarkModal from './MemberRemarkModal';
import MemberMuteModal from './MemberMuteModal';
import MemberViolationHistoryModal from './MemberViolationHistoryModal';
import {
  EMPTY_FILTERS,
  MEMBER_ORDER_STATUS_OPTIONS,
  MEMBER_ROLE_OPTIONS,
  MEMBER_STATUS_OPTIONS,
  PAGE_SIZE,
  buildMemberPageParams,
  formatAllowOrder,
  formatAmount,
  formatContact,
  formatDateCell,
  formatKycName,
  formatMemberSource,
  formatMemberStatus,
  formatOptionalId,
  formatRoleId,
  formatYesNo,
  isPlatformRootMember,
} from './memberConstants';

const TABLE_COLUMNS = [
  '頂級用戶ID',
  '用戶ID',
  '帳號類型',
  '國碼',
  '用戶郵箱/手機號碼',
  '所屬試玩帳號ID',
  '邀請碼',
  '直屬上級ID',
  '可提款金額',
  '可提盈利金額',
  '充值總額',
  '提款中金額',
  '提現總額',
  '是否可提現',
  '提現是否需KYC',
  '用戶狀態',
  '下單狀態',
  '備註',
  '當前流水',
  '最近登入IP',
  '登入IP歸屬地',
  '最近登入時間',
  '註冊IP',
  '註冊IP歸屬地',
  '註冊時間',
  '所屬國家',
  '證件號碼',
  'KYC姓名',
  '用戶來源',
  '操作',
];

export default function MemberListPage() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [records, setRecords] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedIds, setSelectedIds] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [actionRow, setActionRow] = useState(null);
  const [passwordMode, setPasswordMode] = useState('login');
  const [batchModal, setBatchModal] = useState(null);
  const [memberLevelInput, setMemberLevelInput] = useState('');
  const [memberLevelRow, setMemberLevelRow] = useState(null);
  const [withdrawNeedKyc, setWithdrawNeedKyc] = useState(null);

  const selectableRecords = useMemo(
    () => records.filter((row) => !isPlatformRootMember(row.id)),
    [records],
  );

  const allSelected = useMemo(
    () =>
      selectableRecords.length > 0
      && selectableRecords.every((row) => selectedIds.includes(row.id)),
    [selectableRecords, selectedIds],
  );

  async function loadRecords(pageNo = 1, nextFilters = filters) {
    setLoading(true);
    setError('');
    try {
      const data = await adminApi.memberPage(buildMemberPageParams(pageNo, nextFilters, PAGE_SIZE));
      setRecords(data?.records || []);
      setPage(data?.current || pageNo);
      setTotalPages(data?.pages || 1);
      setSelectedIds([]);
    } catch (err) {
      setError(err.message || '載入失敗');
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadRecords(1);
    adminApi
      .tenantConfigOne('kyc')
      .then((data) => setWithdrawNeedKyc(normalizeKycConfig(data?.content || data)))
      .catch(() => setWithdrawNeedKyc(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function updateFilter(key, value) {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }

  function handleSearch(event) {
    event.preventDefault();
    loadRecords(1, filters);
  }

  function handleReset() {
    const reset = { ...EMPTY_FILTERS };
    setFilters(reset);
    setError('');
    loadRecords(1, reset);
  }

  function toggleSelectAll() {
    if (allSelected) {
      setSelectedIds([]);
      return;
    }
    setSelectedIds(selectableRecords.map((row) => row.id));
  }

  function toggleSelect(id) {
    if (isPlatformRootMember(id)) {
      return;
    }
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]));
  }

  async function handleExport(exportCurrentPageOnly) {
    setExporting(true);
    setError('');
    try {
      await adminApi.exportMemberCsvZip({
        ...buildMemberPageParams(page, filters, PAGE_SIZE),
        exportCurrentPageOnly,
        fileName: 'member_list',
      });
    } catch (err) {
      setError(err.message || '匯出失敗');
    } finally {
      setExporting(false);
    }
  }

  async function updateMemberField(row, patch) {
    if (isPlatformRootMember(row.id)) {
      setError('平台根帳號不可執行此操作');
      return;
    }
    setError('');
    try {
      await adminApi.memberUpdate({ id: row.id, ...patch });
      await loadRecords(page, filters);
    } catch (err) {
      setError(err.message || '更新失敗');
    }
  }

  async function handleAction(actionKey, row) {
    switch (actionKey) {
      case 'refresh':
        try {
          const today = new Date().toISOString().slice(0, 10);
          await adminApi.memberRefreshDailyReport({ memberId: row.id, date: today });
          await loadRecords(page, filters);
        } catch (err) {
          setError(err.message || '數據更新失敗');
        }
        break;
      case 'inviterTree':
        setActionRow(row);
        break;
      case 'agent':
        setActionRow({ ...row, _modal: 'agent' });
        break;
      case 'loginPassword':
        setPasswordMode('login');
        setActionRow({ ...row, _modal: 'password' });
        break;
      case 'tradePassword':
        setPasswordMode('trade');
        setActionRow({ ...row, _modal: 'password' });
        break;
      case 'remark':
        setActionRow({ ...row, _modal: 'remark' });
        break;
      case 'balance':
        setActionRow({ ...row, _modal: 'balance' });
        break;
      case 'withdrawAddress':
        navigate(`/system/user-manage/user-withdraw-address-list?memberId=${row.id}`);
        break;
      case 'memberLevel':
        setMemberLevelRow(row);
        setMemberLevelInput(row.memberLevel != null ? String(row.memberLevel) : '');
        break;
      case 'passwordless':
        try {
          const result = await adminApi.memberLoginWithoutPassword(row.id);
          if (result?.h5Url && result?.token) {
            window.open(`${result.h5Url}?token=${encodeURIComponent(result.token)}`, '_blank');
          }
        } catch (err) {
          setError(err.message || '免密登入失敗');
        }
        break;
      case 'violationHistory':
        setActionRow({ ...row, _modal: 'violationHistory' });
        break;
      case 'mute':
        setActionRow({ ...row, _modal: 'mute' });
        break;
      case 'disable':
        try {
          await adminApi.memberDisable(row.id);
          await loadRecords(page, filters);
        } catch (err) {
          setError(err.message || '禁用失敗');
        }
        break;
      case 'banOrder':
        await updateMemberField(row, { allowOrder: 0 });
        break;
      case 'withdrawableProfit':
      case 'turnover':
        setError('此功能尚未開放，請稍後再試');
        break;
      default:
        break;
    }
  }

  async function handleSaveMemberLevel(event) {
    event.preventDefault();
    if (!memberLevelRow) {
      return;
    }
    try {
      await adminApi.memberUpdate({
        id: memberLevelRow.id,
        memberLevel: memberLevelInput === '' ? null : Number(memberLevelInput),
      });
      setMemberLevelRow(null);
      await loadRecords(page, filters);
    } catch (err) {
      setError(err.message || '更新層級失敗');
    }
  }

  const inviterTreeOpen = actionRow && !actionRow._modal;
  const agentOpen = actionRow?._modal === 'agent';
  const passwordOpen = actionRow?._modal === 'password';
  const remarkOpen = actionRow?._modal === 'remark';
  const balanceOpen = actionRow?._modal === 'balance';
  const muteOpen = actionRow?._modal === 'mute';
  const violationOpen = actionRow?._modal === 'violationHistory';

  return (
    <div className="page-card member-list-page">
      <div className="section-header">
        <h2>用戶列表</h2>
      </div>

      <form className="filter-bar filter-bar-wrap" onSubmit={handleSearch}>
        <div className="form-field">
          <label htmlFor="memberRoleId">帳號類型</label>
          <select id="memberRoleId" value={filters.roleId} onChange={(e) => updateFilter('roleId', e.target.value)}>
            {MEMBER_ROLE_OPTIONS.map((item) => (
              <option key={item.value || 'all'} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
        </div>
        <div className="form-field">
          <label htmlFor="memberStatus">用戶狀態</label>
          <select id="memberStatus" value={filters.status} onChange={(e) => updateFilter('status', e.target.value)}>
            {MEMBER_STATUS_OPTIONS.map((item) => (
              <option key={item.value || 'all'} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
        </div>
        <div className="form-field">
          <label htmlFor="memberAllowOrder">下單狀態</label>
          <select
            id="memberAllowOrder"
            value={filters.allowOrder}
            onChange={(e) => updateFilter('allowOrder', e.target.value)}
          >
            {MEMBER_ORDER_STATUS_OPTIONS.map((item) => (
              <option key={item.value || 'all'} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
        </div>
        <div className="form-field">
          <label htmlFor="memberIds">用戶ID</label>
          <input
            id="memberIds"
            value={filters.memberIds}
            onChange={(e) => updateFilter('memberIds', e.target.value)}
            placeholder="多筆用戶ID，逗號分隔"
          />
        </div>
        <div className="form-field">
          <label htmlFor="memberCountryCode">國碼</label>
          <input
            id="memberCountryCode"
            value={filters.countryCode}
            onChange={(e) => updateFilter('countryCode', e.target.value)}
            placeholder="暫不支援篩選"
            disabled
          />
        </div>
        <div className="form-field">
          <label htmlFor="memberEmailPhone">用戶郵箱/手機號</label>
          <input
            id="memberEmailPhone"
            value={filters.emailOrPhone}
            onChange={(e) => updateFilter('emailOrPhone', e.target.value)}
          />
        </div>
        <div className="form-field">
          <label htmlFor="memberPromotionCode">邀請碼</label>
          <input
            id="memberPromotionCode"
            value={filters.promotionCode}
            onChange={(e) => updateFilter('promotionCode', e.target.value)}
          />
        </div>
        <div className="form-field">
          <label htmlFor="memberInviterId">直屬上級用戶ID</label>
          <input
            id="memberInviterId"
            type="number"
            value={filters.inviterId}
            onChange={(e) => updateFilter('inviterId', e.target.value)}
          />
        </div>
        <div className="form-field">
          <label htmlFor="memberTopInviterId">頂級上級用戶ID</label>
          <input
            id="memberTopInviterId"
            type="number"
            value={filters.topInviterId}
            onChange={(e) => updateFilter('topInviterId', e.target.value)}
          />
        </div>
        <div className="form-field">
          <label htmlFor="memberLastLoginIp">最近登入IP</label>
          <input
            id="memberLastLoginIp"
            value={filters.lastLoginIp}
            onChange={(e) => updateFilter('lastLoginIp', e.target.value)}
          />
        </div>
        <div className="form-field">
          <label htmlFor="memberRegisterIp">註冊IP</label>
          <input
            id="memberRegisterIp"
            value={filters.registerIp}
            onChange={(e) => updateFilter('registerIp', e.target.value)}
          />
        </div>
        <div className="form-field">
          <label htmlFor="memberWithdrawAddress">提現地址</label>
          <input
            id="memberWithdrawAddress"
            value={filters.withdrawAddress}
            onChange={(e) => updateFilter('withdrawAddress', e.target.value)}
            placeholder="暫不支援篩選"
            disabled
          />
        </div>
        <div className="form-field">
          <label htmlFor="memberRegStart">註冊時間起</label>
          <input
            id="memberRegStart"
            type="datetime-local"
            value={filters.registrationStartInput}
            onChange={(e) => updateFilter('registrationStartInput', e.target.value)}
          />
        </div>
        <div className="form-field">
          <label htmlFor="memberRegEnd">註冊時間迄</label>
          <input
            id="memberRegEnd"
            type="datetime-local"
            value={filters.registrationEndInput}
            onChange={(e) => updateFilter('registrationEndInput', e.target.value)}
          />
        </div>
        <div className="form-field">
          <label htmlFor="memberLevel">用戶層級</label>
          <input
            id="memberLevel"
            value={filters.memberLevel}
            onChange={(e) => updateFilter('memberLevel', e.target.value)}
            placeholder="暫不支援篩選"
            disabled
          />
        </div>
        <div className="form-field">
          <label htmlFor="memberBalanceMin">可提款金額下限</label>
          <input
            id="memberBalanceMin"
            type="number"
            step="0.01"
            value={filters.balanceMin}
            onChange={(e) => updateFilter('balanceMin', e.target.value)}
          />
        </div>
        <div className="form-field">
          <label htmlFor="memberBalanceMax">可提款金額上限</label>
          <input
            id="memberBalanceMax"
            type="number"
            step="0.01"
            value={filters.balanceMax}
            onChange={(e) => updateFilter('balanceMax', e.target.value)}
          />
        </div>
        <div className="filter-actions">
          <button type="submit" className="btn btn-primary">
            搜索
          </button>
          <button type="button" className="btn btn-secondary" onClick={handleReset}>
            重置
          </button>
        </div>
      </form>

      <div className="member-list-toolbar">
        <button
          type="button"
          className="btn btn-primary btn-sm"
          onClick={() =>
            setBatchModal({
              title: '批量啟用/禁用帳號',
              fields: [
                {
                  key: 'status',
                  label: '用戶狀態',
                  type: 'select',
                  options: [
                    { value: '', label: '不修改' },
                    { value: '1', label: '啟用' },
                    { value: '0', label: '禁用' },
                  ],
                },
              ],
            })
          }
        >
          批量啟用/禁用帳號
        </button>
        <button
          type="button"
          className="btn btn-primary btn-sm"
          onClick={() =>
            setBatchModal({
              title: '批量修改備註',
              fields: [{ key: 'remark', label: '備註', type: 'text' }],
            })
          }
        >
          批量修改備註
        </button>
        <button
          type="button"
          className="btn btn-info btn-sm"
          onClick={() =>
            setBatchModal({
              title: '批量啟用/禁止下單',
              fields: [
                {
                  key: 'allowOrder',
                  label: '下單狀態',
                  type: 'select',
                  options: [
                    { value: '', label: '不修改' },
                    { value: '1', label: '啟用下單' },
                    { value: '0', label: '禁止下單' },
                  ],
                },
              ],
            })
          }
        >
          批量啟用/禁止下單
        </button>
        <button
          type="button"
          className="btn btn-warning btn-sm"
          disabled={selectedIds.length !== 1}
          onClick={() => navigate(`/system/user-manage/balance-adjust?memberId=${selectedIds[0]}`)}
        >
          批量修改餘額
        </button>
        <button
          type="button"
          className="btn btn-success btn-sm"
          disabled={exporting || loading}
          onClick={() => handleExport(true)}
        >
          {exporting ? '匯出中…' : '匯出Excel（當前頁面）'}
        </button>
        <button
          type="button"
          className="btn btn-success btn-sm"
          disabled={exporting || loading}
          onClick={() => handleExport(false)}
        >
          {exporting ? '匯出中…' : '匯出Excel（全部資料）'}
        </button>
      </div>

      {loading && <p>載入中…</p>}
      {error && <div className="alert alert-error">{error}</div>}

      <div className="table-scroll">
        <table className="data-table member-list-table">
          <thead>
            <tr>
              <th>
                <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} aria-label="全選" />
              </th>
              {TABLE_COLUMNS.map((col) => (
                <th key={col}>{col}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {!loading && records.length === 0 ? (
              <tr>
                <td colSpan={TABLE_COLUMNS.length + 1} className="empty-cell">
                  尚無資料
                </td>
              </tr>
            ) : (
              records.map((row) => (
                <tr key={row.id} className={isPlatformRootMember(row.id) ? 'member-row--platform-root' : undefined}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(row.id)}
                      disabled={isPlatformRootMember(row.id)}
                      onChange={() => toggleSelect(row.id)}
                      aria-label={`選擇用戶 ${row.id}`}
                    />
                  </td>
                  <td>{formatOptionalId(row.topInviterId)}</td>
                  <td className="mono-cell">
                    {formatOptionalId(row.id)}
                    {isPlatformRootMember(row.id) ? (
                      <span className="status-badge status-badge--pending member-root-badge">平台根</span>
                    ) : null}
                  </td>
                  <td>{formatRoleId(row.roleId)}</td>
                  <td>—</td>
                  <td className="remark-cell">{formatContact(row)}</td>
                  <td className="mono-cell">{formatOptionalId(row.touristMemberId)}</td>
                  <td>{row.promotionCode || '—'}</td>
                  <td>{formatOptionalId(row.inviterId)}</td>
                  <td>{formatAmount(row.withdrawableBalance ?? row.balance)}</td>
                  <td>{formatAmount(row.lockedPositionProfitAmount)}</td>
                  <td>{formatAmount(row.depositAmount)}</td>
                  <td>—</td>
                  <td>{formatAmount(row.withdrawAmount)}</td>
                  <td>
                    <button
                      type="button"
                      className={`toggle-switch toggle-switch--table ${row.isCanWithdrawal === 1 ? 'is-on' : ''}`}
                      disabled={isPlatformRootMember(row.id)}
                      onClick={() =>
                        updateMemberField(row, { isCanWithdrawal: row.isCanWithdrawal === 1 ? 0 : 1 })
                      }
                      aria-label="切換是否可提現"
                    >
                      <span className="toggle-switch__slider" />
                    </button>
                  </td>
                  <td>{formatYesNo(withdrawNeedKyc?.withdrawNeedKyc)}</td>
                  <td className={row.status === 1 ? 'text-profit' : 'text-loss'}>
                    {formatMemberStatus(row.status)}
                  </td>
                  <td className={row.allowOrder === 1 ? 'text-profit' : 'text-loss'}>
                    {formatAllowOrder(row.allowOrder)}
                  </td>
                  <td className="remark-cell">{row.remark || '—'}</td>
                  <td>—</td>
                  <td className="mono-cell">{row.lastLoginIp || '—'}</td>
                  <td>{row.lastLoginIpAddress || '—'}</td>
                  <td>{formatDateCell(row.lastLoginTime)}</td>
                  <td className="mono-cell">{row.registerIp || '—'}</td>
                  <td>{row.registerIpAddress || '—'}</td>
                  <td>{formatDateCell(row.registrationTime)}</td>
                  <td>{row.country || '—'}</td>
                  <td className="mono-cell">{row.identityNumber || '—'}</td>
                  <td>{formatKycName(row)}</td>
                  <td>{formatMemberSource(row.source)}</td>
                  <td>
                    <MemberActionsMenu row={row} onAction={handleAction} />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Pagination page={page} totalPages={totalPages} onChange={(p) => loadRecords(p, filters)} />

      <MemberInviterTreeModal
        open={Boolean(inviterTreeOpen)}
        row={inviterTreeOpen ? actionRow : null}
        onClose={() => setActionRow(null)}
      />
      <MemberAgentEditModal
        open={agentOpen}
        row={agentOpen ? actionRow : null}
        onClose={() => setActionRow(null)}
        onSaved={() => loadRecords(page, filters)}
      />
      <MemberPasswordModal
        open={passwordOpen}
        row={passwordOpen ? actionRow : null}
        mode={passwordMode}
        onClose={() => setActionRow(null)}
        onSaved={() => loadRecords(page, filters)}
      />
      <MemberRemarkModal
        open={remarkOpen}
        row={remarkOpen ? actionRow : null}
        onClose={() => setActionRow(null)}
        onSaved={() => loadRecords(page, filters)}
      />
      <MemberBalanceAdjustModal
        open={balanceOpen}
        row={balanceOpen ? actionRow : null}
        onClose={() => setActionRow(null)}
        onSuccess={() => loadRecords(page, filters)}
      />
      <MemberMuteModal
        open={muteOpen}
        row={muteOpen ? actionRow : null}
        onClose={() => setActionRow(null)}
        onSaved={() => loadRecords(page, filters)}
      />
      <MemberViolationHistoryModal
        open={violationOpen}
        member={violationOpen ? actionRow : null}
        onClose={() => setActionRow(null)}
      />
      <MemberBatchModal
        open={Boolean(batchModal)}
        title={batchModal?.title}
        selectedIds={selectedIds}
        fields={batchModal?.fields || []}
        onClose={() => setBatchModal(null)}
        onSaved={() => loadRecords(page, filters)}
      />

      {memberLevelRow ? (
        <div className="admin-modal-overlay" role="presentation" onClick={() => setMemberLevelRow(null)}>
          <div className="admin-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal__header">
              <h3>修改用戶層級 [{formatOptionalId(memberLevelRow.id)}]</h3>
              <button type="button" className="admin-modal__close" onClick={() => setMemberLevelRow(null)}>
                ×
              </button>
            </div>
            <form onSubmit={handleSaveMemberLevel}>
              <div className="admin-modal__body">
                <div className="form-field">
                  <label htmlFor="memberLevelValue">用戶層級</label>
                  <input
                    id="memberLevelValue"
                    type="number"
                    value={memberLevelInput}
                    onChange={(e) => setMemberLevelInput(e.target.value)}
                  />
                </div>
              </div>
              <div className="admin-modal__footer">
                <button type="submit" className="btn btn-success">
                  提交
                </button>
                <button type="button" className="btn btn-danger" onClick={() => setMemberLevelRow(null)}>
                  關閉
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
}
